import Dropzone from "react-dropzone";
import { useEffect, useState } from "react";
import { Paper, Box, Typography, IconButton } from "@mui/material";
import { Vocabulary } from "../Utils/Vocabulary";
import { CloudUpload } from "@mui/icons-material";
import styles from "../Styles/dropzone.module.css";
import { DropzoneComponentProps } from "../Utils/Types";
import genericFile from "../Images/Icons/Generic-icon.png";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PreviewPDF from "./Widgets/PreviewPDF";
import { getFileExtensionByFileType } from "../Utils/Utils";

export default function DropzoneComponent(props: DropzoneComponentProps) {
  const [files, setFiles] = useState<any[]>(props.paths || []);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<{
    name: string;
    url: string;
    type: string;
  } | null>(null);

  // Sync files when props.paths becomes available
  useEffect(() => {
    if (props.paths?.length) {
      setFiles(props.paths);
    }
  }, [props.paths]);

  const handleDelete = (indexToDelete: number) => {
    const updatedFiles = files?.filter((_, i) => i !== indexToDelete);
    setFiles(updatedFiles);
    if (props.onSave) {
      props.onSave(updatedFiles);
    }
  };

  const getFileUrl = (file: File | string) => {
    if (typeof file === "string") return file;
    if (file instanceof File) return URL.createObjectURL(file);
    return genericFile;
  };

  const getFileType = (file: File | string) => {
    const fileName = typeof file === "string" ? file : file?.name || "";
    if (!fileName) return "FILE";
    const extension = fileName.split(".").pop()?.toLowerCase();

    switch (extension) {
      case "pdf":
        return "PDF";
      case "doc":
      case "docx":
        return "DOC";
      case "xls":
      case "xlsx":
        return "XLS";
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
      case "bmp":
      case "webp":
        return "IMG";
      default:
        return extension?.toUpperCase() || "FILE";
    }
  };

  const isImageFile = (file: File | string) => {
    const fileName = typeof file === "string" ? file : file?.name || "";
    return fileName.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i);
  };

  const getFileTypeColor = (fileType: string) => {
    switch (fileType) {
      case "PDF":
        return "#d32f2f";
      case "DOC":
        return "#2196f3";
      case "XLS":
        return "#4caf50";
      case "IMG":
        return "#ff9800";
      default:
        return "#757575";
    }
  };

  /**
   * Render the list of uploaded files with enhanced previews
   */
  const renderFiles = () => {
    if (!files?.length) return null;

    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 2,
          mt: 2,
          maxHeight: "300px",
          overflowY: "auto",
        }}
      >
        {files.map((file, index) => {
          const fileType = getFileType(file);
          const fileName = typeof file === "string" ? file : file?.name || "";
          const fileSize =
            typeof file === "string"
              ? ""
              : file?.size
              ? `${(file.size / 1024 / 1024).toFixed(1)} MB`
              : "";

          return (
            <Box
              key={index}
              sx={{
                border: "1px solid #ddd",
                borderRadius: 1,
                p: 1,
                backgroundColor: "white",
                textAlign: "center",
                position: "relative",
              }}
            >
              {/* File Preview Pictogram */}
              <Box sx={{ mb: 1, display: "flex", justifyContent: "center" }}>
                {isImageFile(file) ? (
                  <img
                    src={getFileUrl(file)}
                    alt="Preview"
                    style={{
                      width: "60px",
                      height: "60px",
                      objectFit: "cover",
                      borderRadius: "4px",
                      border: "1px solid #ddd",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: "60px",
                      height: "60px",
                      backgroundColor: getFileTypeColor(fileType),
                      borderRadius: "4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "12px",
                      fontWeight: "bold",
                    }}
                  >
                    {fileType}
                  </Box>
                )}
              </Box>

              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  fontWeight: "medium",
                  mb: 0.5,
                  wordBreak: "break-word",
                  lineHeight: 1.2,
                }}
              >
                {fileName.length > 15
                  ? `${fileName.substring(0, 15)}...`
                  : fileName}
              </Typography>

              {fileSize && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  {fileSize}
                </Typography>
              )}

              <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
                {props.isPreview && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const fileUrl = getFileUrl(file);
                      const fileType =
                        getFileExtensionByFileType(fileName) || "";
                      setSelectedFile({
                        name: fileName,
                        url: fileUrl,
                        type: fileType,
                      });
                      setIsPreviewOpen(true);
                    }}
                    color="primary"
                    title={Vocabulary.preview}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                )}
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDelete(index);
                  }}
                  color="error"
                  title={Vocabulary.delete}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <>
      <Dropzone
        accept={props.acceptedFileTypes}
        maxFiles={props.maxFiles}
        maxSize={props.maxSize}
        multiple={props.multiple}
        onDrop={(acceptedFiles) => {
          let updatedFiles;

          if (props.maxFiles === 1) {
            updatedFiles = acceptedFiles.slice(0, 1);
          } else {
            updatedFiles = [...files, ...acceptedFiles].slice(
              0,
              props.maxFiles
            );
          }

          setFiles(updatedFiles);

          // Remove any existing blob URLs (just keep real strings and Files)
          const cleaned = updatedFiles?.filter(
            (f) =>
              (typeof f === "string" && !f.startsWith("blob:")) ||
              f instanceof File
          );

          props.onSave(cleaned);
        }}
      >
        {({ getRootProps, getInputProps }) => (
          <Paper style={{ textAlign: "center" }}>
            <div {...getRootProps()} className={styles.dropzoneArea}>
              <input {...getInputProps()} />
              {files?.length === 0 ? (
                <>
                  <p>{Vocabulary.dragAndDrop}</p>
                  <CloudUpload fontSize="large" />
                </>
              ) : (
                <>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {files.length} fișier{files.length !== 1 ? "e" : ""}{" "}
                    încărcat{files.length !== 1 ? "e" : ""}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mb: 2 }}
                  >
                    Trageți mai multe fișiere aici sau faceți clic pentru a
                    adăuga
                  </Typography>
                </>
              )}
              {files?.length > 0 && renderFiles()}
              {props?.error?.message && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {props.error.message}
                </Typography>
              )}
            </div>
          </Paper>
        )}
      </Dropzone>
      {selectedFile && isPreviewOpen && (
        <PreviewPDF
          title={selectedFile.name}
          open={isPreviewOpen}
          onClose={() => {
            setIsPreviewOpen(false);
            setSelectedFile(null);
          }}
          url={selectedFile.url}
          htmlContent={""}
        />
      )}
    </>
  );
}
